import React from 'react';
import { Box, Stack, TextField, Button, Alert, Typography } from '@mui/material';
import { Clear as ClearIcon, FilterAlt as FilterIcon } from '@mui/icons-material';
import { UseDateRangeFilterReturn } from '../hooks';

export interface DateRangeFilterProps {
  /** Filter state from useDateRangeFilter hook */
  filter: UseDateRangeFilterReturn;
  /** Label prefix for the filter (default: "Service Date Range") */
  label?: string;
  /** Whether to show the filter icon (default: true) */
  showIcon?: boolean;
}

// Helper to format date for input field (YYYY-MM-DD)
const formatDateForInput = (date: string | null): string => {
  if (!date) return '';
  return date;
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  filter,
  label = 'Service Date Range',
  showIcon = true,
}) => {
  const { value, validationError, hasFilter, setFrom, setTo, clear } = filter;

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFrom(e.target.value);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTo(e.target.value);
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {showIcon && <FilterIcon sx={{ color: '#64748b' }} />}
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
          {label}:
        </Typography>
        <TextField
          type="date"
          size="small"
          label="From Date"
          value={formatDateForInput(value.from)}
          onChange={handleDateFromChange}
          InputLabelProps={{ shrink: true }}
          error={!!validationError}
          sx={{
            width: 170,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: validationError ? '#d32f2f' : '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: validationError ? '#d32f2f' : '#3b82f6',
              },
              '&.Mui-focused fieldset': {
                borderColor: validationError ? '#d32f2f' : '#3b82f6',
              },
            },
          }}
        />
        <TextField
          type="date"
          size="small"
          label="To Date"
          value={formatDateForInput(value.to)}
          onChange={handleDateToChange}
          InputLabelProps={{ shrink: true }}
          error={!!validationError}
          sx={{
            width: 170,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: validationError ? '#d32f2f' : '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: validationError ? '#d32f2f' : '#3b82f6',
              },
              '&.Mui-focused fieldset': {
                borderColor: validationError ? '#d32f2f' : '#3b82f6',
              },
            },
          }}
        />
        {hasFilter && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={clear}
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: '#f8fafc',
              },
            }}
          >
            Clear Dates
          </Button>
        )}
      </Stack>
      {validationError && (
        <Alert severity="error" sx={{ mt: 1, maxWidth: 400 }}>
          {validationError}
        </Alert>
      )}
    </Box>
  );
};

export default DateRangeFilter;
